import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Lock, ArrowLeft, Eye, EyeOff, Mail, KeyRound } from "lucide-react";
import { Button } from "@/components/ui/button";
import Navigation from "@/components/Navigation";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminLogin = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(true);
  const [rememberMe, setRememberMe] = useState(false);
  
  // Password reset states
  const [resetMode, setResetMode] = useState(false);
  const [resetStep, setResetStep] = useState('request');
  const [resetCode, setResetCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);

  useEffect(() => {
    // Load remembered credentials
    const savedEmail = localStorage.getItem('adminRememberEmail');
    if (savedEmail) {
      setEmail(savedEmail);
      setRememberMe(true);
    }
    
    // Check if already logged in with a valid session
    const checkSession = async () => {
      const token = localStorage.getItem('adminToken');
      if (token) {
        try {
          // Test if token is valid (cookie or header)
          await axios.get(`${API}/events`, {
            headers: { Authorization: `Bearer ${token}` },
            withCredentials: true
          });
          // Token is valid, redirect to dashboard
          navigate('/admin/dashboard');
        } catch (error) {
          // Token invalid, clear storage
          localStorage.removeItem('adminToken');
          localStorage.removeItem('adminData');
          localStorage.removeItem('adminEmail');
        }
      }
      setChecking(false);
    };
    
    checkSession();
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/admin/login`, { email, password }, { withCredentials: true });
      
      // Store user data (token is now in httpOnly cookie)
      localStorage.setItem('adminToken', response.data.access_token);  // Keep for backward compat display
      localStorage.setItem('adminData', JSON.stringify(response.data.user_data));
      localStorage.setItem('adminEmail', email.toLowerCase());
      
      // Handle remember me
      if (rememberMe) {
        localStorage.setItem('adminRememberEmail', email.toLowerCase());
      } else {
        localStorage.removeItem('adminRememberEmail');
      }
      
      toast.success("Login successful!");
      navigate('/admin/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || "Login failed. Check your credentials.");
    } finally {
      setLoading(false);
    }
  };

  const handleRequestReset = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error("Please enter your email address");
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(`${API}/admin/request-password-reset`, { email }, { withCredentials: true });
      toast.success("If this email is registered as admin, you will receive a reset code.");
      setResetStep('confirm');
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to request password reset");
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmReset = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmNewPassword) {
      toast.error("Passwords do not match");
      return;
    }
    
    if (newPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(`${API}/admin/reset-password-with-code`, {
        email,
        reset_code: resetCode,
        new_password: newPassword
      }, { withCredentials: true });
      toast.success("Password reset successfully! You can now log in.");
      setResetMode(false);
      setResetStep('request');
      setResetCode("");
      setNewPassword("");
      setConfirmNewPassword("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reset password");
    } finally {
      setLoading(false);
    }
  };

  if (checking) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-zinc-500 text-xl">Loading...</div>
      </div>
    );
  }

  // Password Reset Mode
  if (resetMode) {
    return (
      <div className="min-h-screen bg-black">
        <Navigation />
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
          <Button
            variant="ghost"
            onClick={() => { setResetMode(false); setResetStep('request'); }}
            className="mb-6 text-zinc-300 hover:text-crew-yellow"
          >
            <ArrowLeft className="mr-2 h-4 w-4" /> Back to Login
          </Button>

          <div className="max-w-md mx-auto">
            <div className="bg-crew-zinc border border-zinc-800 rounded-sm p-8">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-14 h-14 bg-zinc-800 rounded-sm flex items-center justify-center">
                  <KeyRound className="w-7 h-7 text-crew-yellow" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-white uppercase tracking-tight">
                    Reset Password
                  </h1>
                  <p className="text-zinc-400 text-sm">Administrator Account</p>
                </div>
              </div>

              {resetStep === 'request' ? (
                <form onSubmit={handleRequestReset} className="space-y-6">
                  <div>
                    <Label htmlFor="email" className="text-zinc-200 font-bold uppercase text-xs">Admin Email</Label>
                    <Input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      placeholder="admin@email.com"
                      className="bg-black border-b-2 border-zinc-800 focus:border-crew-yellow text-white rounded-none px-0 py-4 placeholder:text-zinc-500 border-t-0 border-x-0"
                    />
                  </div>
                  
                  <p className="text-zinc-400 text-sm">
                    Enter your administrator email address and we'll send you a 6-digit code to reset your password.
                  </p>

                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-crew-yellow text-black font-bold uppercase hover:bg-white py-6"
                  >
                    {loading ? "Sending..." : "Send Reset Code"}
                  </Button>
                </form>
              ) : (
                <form onSubmit={handleConfirmReset} className="space-y-6">
                  <div className="bg-green-500/10 border border-green-500/30 rounded p-3 mb-4">
                    <p className="text-green-400 text-sm">
                      <Mail className="inline w-4 h-4 mr-1" />
                      Check your email for the 6-digit reset code
                    </p>
                  </div>
                  
                  <div>
                    <Label htmlFor="resetCode" className="text-zinc-200 font-bold uppercase text-xs">Reset Code</Label>
                    <Input
                      id="resetCode"
                      type="text"
                      value={resetCode}
                      onChange={(e) => setResetCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      required
                      placeholder="Enter 6-digit code"
                      maxLength={6}
                      className="bg-black border-b-2 border-zinc-800 focus:border-crew-yellow text-white rounded-none px-0 py-4 placeholder:text-zinc-500 border-t-0 border-x-0 text-center text-2xl tracking-widest font-mono"
                    />
                  </div>

                  <div>
                    <Label htmlFor="newPassword" className="text-zinc-200 font-bold uppercase text-xs">New Password</Label>
                    <div className="relative">
                      <Input
                        id="newPassword"
                        type={showNewPassword ? "text" : "password"}
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        required
                        minLength={6}
                        placeholder="Minimum 6 characters"
                        className="bg-black border-b-2 border-zinc-800 focus:border-crew-yellow text-white rounded-none px-0 py-4 placeholder:text-zinc-500 border-t-0 border-x-0 pr-10"
                      />
                      <button
                        type="button"
                        onClick={() => setShowNewPassword(!showNewPassword)}
                        className="absolute right-0 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-crew-yellow p-2"
                      >
                        {showNewPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                      </button>
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="confirmNewPassword" className="text-zinc-200 font-bold uppercase text-xs">Confirm New Password</Label>
                    <Input
                      id="confirmNewPassword"
                      type="password"
                      value={confirmNewPassword}
                      onChange={(e) => setConfirmNewPassword(e.target.value)}
                      required
                      minLength={6}
                      placeholder="Re-enter new password"
                      className="bg-black border-b-2 border-zinc-800 focus:border-crew-yellow text-white rounded-none px-0 py-4 placeholder:text-zinc-500 border-t-0 border-x-0"
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={loading || resetCode.length !== 6}
                    className="w-full bg-crew-yellow text-black font-bold uppercase hover:bg-white py-6"
                  >
                    {loading ? "Resetting..." : "Reset Password"}
                  </Button>
                  
                  <button
                    type="button"
                    onClick={() => setResetStep('request')}
                    className="w-full text-zinc-400 hover:text-crew-yellow text-sm"
                  >
                    Didn't receive the code? Send again
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Normal Login Mode
  return (
    <div className="min-h-screen bg-black">
      <Navigation />
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
        <Button
          variant="ghost"
          onClick={() => navigate('/')}
          className="mb-6 text-zinc-300 hover:text-crew-yellow"
        >
          <ArrowLeft className="mr-2 h-4 w-4" /> Back to Home
        </Button>

        <div className="max-w-md mx-auto">
          <div className="bg-crew-zinc border border-zinc-800 rounded-sm p-8">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-14 h-14 bg-zinc-800 rounded-sm flex items-center justify-center">
                <Lock className="w-7 h-7 text-crew-yellow" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-white uppercase tracking-tight">
                  Admin Login
                </h1>
                <p className="text-zinc-400 text-sm">Access administrator dashboard</p>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <Label htmlFor="email" className="text-zinc-200 font-bold uppercase text-xs">Email Address</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  placeholder="admin@email.com"
                  className="bg-black border-b-2 border-zinc-800 focus:border-crew-yellow text-white rounded-none px-0 py-4 placeholder:text-zinc-500 border-t-0 border-x-0"
                  data-testid="admin-email-input"
                />
              </div>

              <div>
                <Label htmlFor="password" className="text-zinc-200 font-bold uppercase text-xs">Password</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    placeholder="••••••••"
                    className="bg-black border-b-2 border-zinc-800 focus:border-crew-yellow text-white rounded-none px-0 py-4 placeholder:text-zinc-500 border-t-0 border-x-0 pr-10"
                    data-testid="admin-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-0 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-crew-yellow p-2"
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="rememberMe"
                  checked={rememberMe}
                  onCheckedChange={setRememberMe}
                  className="border-zinc-600 data-[state=checked]:bg-crew-yellow data-[state=checked]:border-crew-yellow"
                  data-testid="admin-remember-me"
                />
                <Label htmlFor="rememberMe" className="text-zinc-300 text-sm cursor-pointer">
                  Remember me
                </Label>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-crew-yellow text-black font-bold uppercase hover:bg-white py-6"
                data-testid="admin-login-button"
              >
                {loading ? "Signing in..." : "Sign In"}
              </Button>
            </form>
            
            <div className="mt-6 text-center">
              <button
                type="button"
                onClick={() => setResetMode(true)}
                className="text-zinc-400 hover:text-crew-yellow text-sm underline"
                data-testid="admin-forgot-password-link"
              >
                Forgot your password?
              </button>
            </div>

            <div className="mt-8 pt-6 border-t border-zinc-800 text-center">
              <p className="text-zinc-400 text-sm">
                For authorized administrators only.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;
